// @flow
import { withProps, compose } from 'recompose'
import LocationBox from '../forms/location-box'
import isFunction from 'lodash/isFunction'
import withGoogleAPI from '../../services/maps/withGoogleAPI'

type Fields = {
  input: {
    name: string,
    value: string,
    onChange: () => any
  },
  meta: {
    error: string,
    dirty: boolean,
    invalid: boolean,
    submitFailed: boolean,
    touched: boolean
  }
}

type ProvidedProps = {
  location: Fields,
  lat: Fields,
  lng: Fields,
  country: Fields,
  countryCode: Fields
}

type ReduxFields = {
  onChange: () => any,
  onChangeLat: () => any,
  onChangeLong: () => any,
  onCountryChange: () => any,
  onCountryCodeChange: () => any,
  value: string,
  lat: string,
  lng: string,
  country: string,
  countryCode: string,
  errorText: string,
  hasError: boolean
}

// this decorator is valid for a <Fields names={['location','lat','lng', 'country', 'countryCode']} />
// redux-form component to change 5 fields at once
export const withReduxFields = withProps(
  (props: ProvidedProps): ReduxFields => {
    const { location, lat, lng, country, countryCode } = props
    return {
      onChange: location && location.input.onChange,
      onChangeLat: lat && lat.input.onChange,
      onChangeLong: lng && lng.input.onChange,
      onCountryChange: country && country.input.onChange,
      onCountryCodeChange: countryCode && countryCode.input.onChange,
      name: location && location.input.name,
      value: location && location.input.value,
      lat: lat && lat.input.value,
      lng: lng && lng.input.value,
      country: country && country.input.value,
      countryCode: countryCode && countryCode.input.value,
      errorText: location && location.meta.error,
      hasError: Boolean(
        (location.meta.dirty || location.meta.submitFailed) &&
          (location.meta.invalid || location.meta.error)
      )
    }
  }
)

type AddressComponent = {
  long_name: string,
  short_name: string,
  types: Array<string>
}

type Result = {
  address_components: Array<AddressComponent>
}

// Extract country from a Places result
// https://developers.google.com/maps/documentation/geocoding/start#sample-request
export function extractCountryData(result: Result) {
  const countryComponent =
    result &&
    result.address_components &&
    result.address_components.find(component =>
      component.types.includes('country')
    )
  return countryComponent
    ? {
        country: countryComponent.long_name,
        countryCode: countryComponent.short_name
      }
    : {
        country: 'Unknown',
        countryCode: null
      }
}

export const withRefsAndChangeHandlers = withProps(props => {
  const refs = {
    searchBox: null
  }
  return {
    onSearchBoxMounted(node) {
      refs.searchBox = node
    },
    onInputFocus(e) {
      e.target.select()
    },
    onInputChange(e) {
      const value = e.target.value
      isFunction(props.onChange) && props.onChange(value)
      isFunction(props.onChangeLat) && props.onChangeLat('')
      isFunction(props.onChangeLong) && props.onChangeLong('')
      isFunction(props.onCountryChange) && props.onCountryChange('')
      isFunction(props.onCountryCodeChange) && props.onCountryCodeChange('')
    },
    onAddressChanged(e) {
      const places = refs.searchBox && refs.searchBox.getPlaces()
      const result = places && places[0]
      const location = result && result.formatted_address
      const lat = result && result.geometry.location.lat()
      const lng = result && result.geometry.location.lng()
      const countryData = result && extractCountryData(result)
      if (isFunction(props.onChange)) {
        location && props.onChange(location)
      }
      if (isFunction(props.onChangeLat)) {
        lat && props.onChangeLat(lat.toString())
      }
      if (isFunction(props.onChangeLong)) {
        lng && props.onChangeLong(lng.toString())
      }
      if (isFunction(props.onCountryChange)) {
        countryData && props.onCountryChange(countryData.country)
      }
      if (isFunction(props.onCountryCodeChange)) {
        countryData && props.onCountryCodeChange(countryData.countryCode)
      }
    },
    onPositionChanged({ lat, lng }) {
      if (isFunction(props.onChangeLat)) {
        props.onChangeLat(lat.toString())
      }
      if (isFunction(props.onChangeLong)) {
        props.onChangeLong(lng.toString())
      }
    }
  }
})

export const decorator = compose(
  withGoogleAPI,
  withReduxFields,
  withRefsAndChangeHandlers
)
export default decorator(LocationBox)



// WEBPACK FOOTER //
// ./src/ui/maps/LocationSearchBox.js